/**
 * ManageUserRolesModal Component
 * Modal for managing organization-level roles for an existing user
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, Shield, Plus, Star, Trash2, AlertCircle, Loader2, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { userManagementAPI, UserRoleAssignment, RoleAuditEntry } from '@/services/api/userManagement';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { isInternalSystemRole } from '@/utils/superAdminPolicy';

/** Format an ISO timestamp as a short, locale-aware "assigned by / on" string. */
const formatAuditDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const AUDIT_ACTION_LABELS: Record<string, string> = {
  assigned: 'Assigned',
  removed: 'Removed',
  set_primary: 'Set primary',
};

interface ManageUserRolesModalProps {
  userId: number;
  userName: string;
  cboId: number;
  onClose: () => void;
  onRolesChanged?: () => void;
}

const SYSTEM_ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  org_admin: 'Org Admin',
  ops_admin: 'Ops Admin',
  program_manager: 'Program Manager',
  supervisor: 'Supervisor',
  staff: 'Staff',
  viewer: 'Viewer',
};

export const ManageUserRolesModal: React.FC<ManageUserRolesModalProps> = ({
  userId,
  userName,
  cboId,
  onClose,
  onRolesChanged,
}) => {
  const { t } = useAppTranslation();
  const [roles, setRoles] = useState<UserRoleAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoleType, setSelectedRoleType] = useState('');
  const [allOrgRoles, setAllOrgRoles] = useState<{ id: number; role_type: string; display_name_en: string; system_role: string }[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [audit, setAudit] = useState<RoleAuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  useEffect(() => {
    userManagementAPI.getAllOrgRoles(cboId)
      .then(setAllOrgRoles)
      .catch(() => {})
      .finally(() => setRolesLoading(false));
  }, [cboId]);

  // Roles available to add (exclude already-assigned, and never the internal
  // platform tiers — TRUS-513). Every org carries a seeded role_terminology row
  // with system_role='super_admin' (historical; kept active because nav config
  // scopes its overrides to it), so without this filter "Super Admin" appeared
  // as an assignable role in every org's Manage Roles modal. The backend refuses
  // it anyway (INTERNAL_ONLY_SYSTEM_ROLES in user_organization_roles.py), so the
  // option could only ever produce a 400 — and it advertised a grant path that
  // is supposed to exist only as an approved invitation in CBO 1.
  const availableRoles = useMemo(() => {
    const assignedTypes = new Set(roles.map(r => r.role_type));
    return allOrgRoles.filter(
      r => !assignedTypes.has(r.role_type) && !isInternalSystemRole(r.system_role),
    );
  }, [allOrgRoles, roles]);

  const loadAudit = async () => {
    try {
      setAuditLoading(true);
      const data = await userManagementAPI.getUserRoleAudit(cboId, userId);
      setAudit(data);
    } catch {
      // Audit is non-critical; leave it empty on failure.
      setAudit([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userManagementAPI.getUserRoles(cboId, userId);
      setRoles(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
    loadAudit();
  };

  useEffect(() => {
    loadRoles();
  }, [userId, cboId]);

  const handleAddRole = async () => {
    if (!selectedRoleType) return;
    try {
      setSaving(true);
      setError(null);
      await userManagementAPI.addUserRole(cboId, userId, {
        role_type: selectedRoleType,
        is_primary: roles.length === 0,
      });
      setSelectedRoleType('');
      await loadRoles();
      onRolesChanged?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add role');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRole = async (roleId: number) => {
    if (roles.length <= 1) return;
    try {
      setSaving(true);
      setError(null);
      await userManagementAPI.removeUserRole(cboId, userId, roleId);
      await loadRoles();
      onRolesChanged?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to remove role');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPrimary = async (roleId: number) => {
    try {
      setSaving(true);
      setError(null);
      await userManagementAPI.setUserPrimaryRole(cboId, userId, roleId);
      await loadRoles();
      onRolesChanged?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to set primary role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-3">
            <Shield className="w-6 h-6 text-purple-600 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('admin.manage_roles', 'Manage Roles')}
              </h2>
              <p className="text-sm text-gray-500 mt-1">{userName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 px-3 py-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Current Roles */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              {t('admin.current_roles', 'Current Roles')}
            </label>
            {loading ? (
              <div className="flex items-center justify-center py-6 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : roles.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">
                {t('admin.no_roles_assigned', 'No roles assigned')}
              </p>
            ) : (
              <div className="space-y-2">
                {roles.map((role) => (
                  <div
                    key={role.id}
                    className="flex items-center gap-3 px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50"
                  >
                    {/* Primary star */}
                    <button
                      onClick={() => !role.is_primary && handleSetPrimary(role.id)}
                      disabled={role.is_primary || saving}
                      title={role.is_primary ? 'Primary role' : 'Set as primary'}
                      className={`p-0.5 rounded transition-colors ${
                        role.is_primary
                          ? 'text-yellow-500 cursor-default'
                          : 'text-gray-300 hover:text-yellow-400 cursor-pointer'
                      }`}
                    >
                      <Star className={`w-4 h-4 ${role.is_primary ? 'fill-current' : ''}`} />
                    </button>

                    {/* Role info */}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {role.display_name_en}
                      </span>
                      {role.is_primary && (
                        <span className="ml-2 text-[10px] font-medium text-yellow-600 dark:text-yellow-400 uppercase">
                          {t('admin.primary', 'Primary')}
                        </span>
                      )}
                      {(role.assigned_by_name || role.assigned_at) && (
                        <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                          {role.assigned_by_name
                            ? `${t('admin.assigned_by', 'Assigned by')} ${role.assigned_by_name}`
                            : t('admin.assigned', 'Assigned')}
                          {role.assigned_at ? ` · ${formatAuditDate(role.assigned_at)}` : ''}
                        </div>
                      )}
                    </div>

                    {/* System role badge */}
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300">
                      {SYSTEM_ROLE_LABELS[role.system_role] || role.system_role}
                    </span>

                    {/* Remove button */}
                    <button
                      onClick={() => handleRemoveRole(role.id)}
                      disabled={roles.length <= 1 || saving}
                      title={roles.length <= 1 ? 'Cannot remove last role' : 'Remove role'}
                      className={`p-1 rounded transition-colors ${
                        roles.length <= 1
                          ? 'text-gray-200 dark:text-gray-600 cursor-not-allowed'
                          : 'text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30'
                      }`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Role */}
          {availableRoles.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                {t('admin.add_role', 'Add Role')}
              </label>
              <div className="flex gap-2">
                <select
                  value={selectedRoleType}
                  onChange={(e) => setSelectedRoleType(e.target.value)}
                  disabled={saving || rolesLoading}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 dark:bg-gray-700 disabled:opacity-50"
                >
                  <option value="">{t('admin.select_role', 'Select a role...')}</option>
                  {availableRoles.map((role) => (
                    <option key={role.id} value={role.role_type}>
                      {role.display_name_en} — {SYSTEM_ROLE_LABELS[role.system_role] || role.system_role}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAddRole}
                  disabled={!selectedRoleType || saving}
                  className="flex items-center gap-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {t('admin.add', 'Add')}
                </button>
              </div>
            </div>
          )}

          {/* Info */}
          <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
            {t('admin.roles_info', 'The primary role determines the user\'s default permissions. Additional roles expand access using union/most-permissive-wins strategy.')}
          </p>

          {/* Assignment history (audit log) */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <button
              type="button"
              onClick={() => setShowAudit((v) => !v)}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              {showAudit ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <Clock className="w-4 h-4" />
              <span>{t('admin.assignment_history', 'Assignment history')}</span>
              {audit.length > 0 && (
                <span className="ml-1 text-xs text-gray-400">({audit.length})</span>
              )}
            </button>

            {showAudit && (
              <div className="mt-3">
                {auditLoading ? (
                  <div className="flex items-center justify-center py-4 text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                ) : audit.length === 0 ? (
                  <p className="text-xs text-gray-400 dark:text-gray-500 py-2 text-center">
                    {t('admin.no_history', 'No assignment history yet.')}
                  </p>
                ) : (
                  <ul className="space-y-2 max-h-56 overflow-auto pr-1">
                    {audit.map((entry) => (
                      <li key={entry.id} className="flex items-start gap-2 text-xs">
                        <span
                          className={`mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded font-medium ${
                            entry.action === 'removed'
                              ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                              : entry.action === 'set_primary'
                              ? 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          }`}
                        >
                          {t(`admin.audit_action_${entry.action}`, AUDIT_ACTION_LABELS[entry.action] || entry.action)}
                        </span>
                        <div className="flex-1 min-w-0 text-gray-600 dark:text-gray-300">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {entry.role_display_name || entry.role_type || t('admin.role', 'role')}
                          </span>
                          <span className="text-gray-400 dark:text-gray-500">
                            {' · '}
                            {entry.actor_name
                              ? `${t('admin.by', 'by')} ${entry.actor_name}`
                              : t('admin.by_system', 'by system')}
                            {' · '}
                            {formatAuditDate(entry.created_at)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-white border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 transition-colors"
          >
            {t('admin.done', 'Done')}
          </button>
        </div>
      </div>
    </div>
  );
};
